import React, { useState } from 'react';
import { auth, db, appId } from '../utils/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Icons } from './Icons';

const LoginGate = ({ onSuccess, onClose, setHandle, setClearance }) => {
    const [stage, setStage] = useState(localStorage.getItem('ts_resonance') === 'cleared' ? 'auth' : 'resonance');
    const [mode, setMode] = useState('login'); 
    const [resonanceCode, setResonanceCode] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localHandle, setLocalHandle] = useState('');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleResonance = (e) => {
        e.preventDefault();
        if (['1956', 'DOORS', 'ADMIN'].includes(resonanceCode.toUpperCase())) { 
            localStorage.setItem('ts_resonance', 'cleared'); 
            setStage('auth'); 
            setError(false); 
        } 
        else { setError('FREQUENCY MISMATCH'); }
    };
    
    const handleReset = async () => {
        let resetEmail = email || window.prompt("Enter email for password reset:");
        if (!resetEmail) return;
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            alert(`RESET SIGNAL SENT TO ${resetEmail}`);
        } catch (e) { 
            console.error(e); setError('RESET FAILED: ' + e.message); 
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true); setError(false);
        try {
            if (mode === 'register') {
                if (!localHandle) { setError('HANDLE REQUIRED'); setLoading(false); return; }
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', cred.user.uid), {
                    handle: localHandle, email: email, status: 'PENDING', clearance: 'LEVEL 1', timestamp: serverTimestamp()
                });
                alert("REGISTRATION SUCCESSFUL. IDENTITY PENDING ADMIN APPROVAL.");
                await signOut(auth); setMode('login'); setLoading(false);
            } else {
                const cred = await signInWithEmailAndPassword(auth, email, password);
                const userDoc = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', cred.user.uid));
                if (userDoc.exists()) {
                    const d = userDoc.data();
                    if (d.status === 'PENDING') throw new Error("IDENTITY PENDING APPROVAL");
                    setHandle(d.handle || 'Operative');
                    setClearance(d.clearance || 'LEVEL 1');
                    if (d.clearance === 'FIELD OP' || d.clearance === 'LEVEL 0') {
                        onSuccess('unauthorized');
                    } else {
                        onSuccess('hub');
                    }
                } else {
                    setHandle(email.split('@')[0]); 
                    setClearance('LEVEL 1');
                    onSuccess('hub');
                }
            }
        } catch (err) { 
            console.error(err); setError(err.message.includes("PENDING") ? "ACCESS DENIED: PENDING" : "AUTH FAILED"); setLoading(false); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 font-sans text-white">
            <div className="w-full max-w-md bg-[#0a0a0a] border border-gray-800 p-10 shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-white"><Icons.X /></button>
                <div className="text-center mb-10">
                    <div className="h-16 w-16 mx-auto text-white mb-6">
                        <Icons.TriangleLogo className="w-full h-full" />
                    </div>
                    <h2 className="text-xl font-black tracking-[0.3em] uppercase">TERTIARY GATE</h2>
                    <p className="text-[10px] text-gray-500 mt-2 font-bold tracking-widest uppercase">{stage === 'resonance' ? 'Frequency Check' : 'Identity Verification'}</p>
                </div>
                {stage === 'resonance' ? (
                    <form onSubmit={handleResonance} className="space-y-6">
                        <input type="password" value={resonanceCode} onChange={(e) => { setResonanceCode(e.target.value); setError(false); }} className="w-full bg-[#111] border-b-2 border-gray-800 py-3 px-2 text-white font-mono text-center focus:border-red-600 focus:outline-none text-lg tracking-widest" placeholder="####" autoFocus />
                        {error && <div className="text-red-600 text-xs font-bold text-center animate-pulse">{error}</div>}
                        <button type="submit" className="w-full bg-red-900/20 border border-red-900/50 text-red-500 py-4 font-black uppercase tracking-[0.2em] hover:bg-red-900 hover:text-white transition-all mt-4 text-xs">VERIFY FREQUENCY</button>
                    </form>
                ) : (
                    <form onSubmit={handleAuth} className="space-y-6">
                        <div className="space-y-4">
                            {mode === 'register' && <input type="text" value={localHandle} onChange={(e) => setLocalHandle(e.target.value)} maxLength={40} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="HANDLE" />}
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="EMAIL" />
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="PASSWORD" />
                        </div>
                        {error && <div className="text-red-600 text-xs font-bold text-center animate-pulse">{error}</div>}
                        <button type="submit" disabled={loading} className="w-full bg-blue-900/20 border border-blue-900/50 text-blue-500 py-4 font-black uppercase tracking-[0.2em] hover:bg-blue-900 hover:text-white transition-all disabled:opacity-50 text-xs">{loading ? 'PROCESSING...' : (mode === 'register' ? 'SUBMIT' : 'LOGIN')}</button>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-800">
                            <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(false); }} className="text-[10px] uppercase font-bold tracking-widest text-gray-500 hover:text-white transition-colors">{mode === 'login' ? 'New Operative? Register' : 'Existing? Login'}</button>
                            {mode === 'login' && <button type="button" onClick={handleReset} className="text-[10px] uppercase font-bold tracking-widest text-red-500 hover:text-red-400 transition-colors">Forgot Password?</button>}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default LoginGate;